import { JsonFetcher } from "../json_fetcher";

// AuthClient — the shared HEADLESS auth engine
// (plans/2026-07-11--shared-auth-client-otp-refactor.md).
//
// This is the "thinking" half of sign-in with NO screen of its own: it owns all
// server communication (request code, verify code, verify password, logout), the
// resend-cooldown clock, and the cross-surface success announce. It has no DOM,
// no markup, no styling, no knowledge of which sub-forms a host renders. Each host
// (Rankometer, AccountPanel, MemberOfferAcceptor) owns its own screen and state
// machine and just asks this engine to do the work, then renders the returned
// statusCode however it likes (via the shared auth_messages catalogue).
//
// It supersedes the shared inline login *widget* — the DOM the three hosts were
// fighting over is gone; only the security-sensitive server flow is single-sourced.
//
// Enumeration-uniform (load-bearing): no method branches on "this email is
// registered". `Otp::Issuer` returns CODE_SENT for known AND unknown emails; the
// ONE disclosure is HAS_USER_ACCOUNT (a password user, matching /door/reclaim_person).
// `verify_password` returns INVALID_CREDENTIALS uniformly for unknown-email and
// wrong-password, so it leaks nothing either.
//
// Cookie-store-split hard constraint: every endpoint URL supplied to the
// constructor is an ABSOLUTE https://www.rankers.co.nz URL. A relative URL on a
// campingnz://-loaded page is intercepted by the native offline scheme handler
// into a cookieless jar and always reads/writes the wrong cookie store. Absolute
// HTTPS keeps every request a direct-HTTPS fetch carrying the WKWebView /
// CookieManager `itchi` cookie. See doc/README--native_app_integration.md.
//
// Offline-first: NO method ever rejects. A timeout or unreachable server resolves
// to { statusCode: NETWORK_ERROR } like any other status, so hosts render it
// through the same state-machine path as every outcome — no hung spinner, no
// dead-end, no divergent catch handling.

// The full statusCode vocabulary the client can emit. Every value here MUST have a
// catalogue entry in auth_messages.js (no code can render an empty status).
export const AuthStatus = {
  // requestCode
  CODE_SENT: "CODE_SENT",
  HAS_USER_ACCOUNT: "HAS_USER_ACCOUNT",
  THROTTLED: "THROTTLED",
  INVALID_EMAIL: "INVALID_EMAIL",
  // verifyCode / verifyPassword
  AUTHENTICATED: "AUTHENTICATED",
  INVALID_CODE: "INVALID_CODE",
  EXPIRED_CODE: "EXPIRED_CODE",
  TOO_MANY_ATTEMPTS: "TOO_MANY_ATTEMPTS",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  // logout
  LOGGED_OUT: "LOGGED_OUT",
  // requestPasswordReset (enumeration-uniform — always RESET_SENT for any address)
  RESET_SENT: "RESET_SENT",
  // transport / fallback
  NETWORK_ERROR: "NETWORK_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

// The resend cooldown mirrors Otp::Issuer::SEND_COOLDOWN (60s) — one source of
// truth for the timing policy, matching the server's send-cooldown window.
export const AUTH_COOLDOWN_MS = 60000;
const REQUEST_TIMEOUT_MS = 15000;

export class AuthClient {
  // options: { requestOtpUrl, verifyOtpUrl, verifyPasswordUrl, logoutUrl,
  // requestPasswordResetUrl } — absolute-https door/ endpoints, delivered via each
  // host's dataset config node. requestPasswordResetUrl is optional (only the
  // MemberOfferAcceptor's in-card forgot-password panel uses it today).
  constructor(options = {}) {
    this.requestOtpUrl = options.requestOtpUrl;
    this.verifyOtpUrl = options.verifyOtpUrl;
    this.verifyPasswordUrl = options.verifyPasswordUrl;
    this.logoutUrl = options.logoutUrl;
    this.requestPasswordResetUrl = options.requestPasswordResetUrl;

    this.COOLDOWN_MS = AUTH_COOLDOWN_MS;
    // Timestamp (ms) before which a resend is refused. 0 = resend allowed now.
    this.resendAvailableAt = 0;
  }

  // --- Server flows ----------------------------------------------------------

  // → { statusCode: CODE_SENT | HAS_USER_ACCOUNT | THROTTLED | INVALID_EMAIL | NETWORK_ERROR }
  async requestCode(email) {
    const data = await this.postJson(this.requestOtpUrl, { email: email });
    if (!data) return { statusCode: AuthStatus.NETWORK_ERROR };

    const statusCode = data.status || AuthStatus.UNKNOWN_ERROR;
    switch (statusCode) {
      case AuthStatus.CODE_SENT:
        // A fresh code was emailed → start the 60s resend cooldown.
        this.startCooldown();
        break;
      case AuthStatus.THROTTLED:
        // A code was already sent within the window — still valid, still enterable.
        // Start a cooldown ONLY if one isn't already ticking; leave an in-progress
        // one running (porting the THROTTLED-stranding fix so the resend button
        // doesn't invite an immediate re-throttle on a fresh instance).
        if (this.canResend()) this.startCooldown();
        break;
      default:
        break;
    }
    return { statusCode: statusCode };
  }

  // → { statusCode: AUTHENTICATED | INVALID_CODE | EXPIRED_CODE | TOO_MANY_ATTEMPTS | NETWORK_ERROR, personId? }
  async verifyCode(email, code) {
    const data = await this.postJson(this.verifyOtpUrl, { email: email, code: code });
    if (!data) return { statusCode: AuthStatus.NETWORK_ERROR };
    return this.resolveVerify(data);
  }

  // → { statusCode: AUTHENTICATED | INVALID_CREDENTIALS | TOO_MANY_ATTEMPTS | NETWORK_ERROR, personId? }
  async verifyPassword(email, password) {
    const data = await this.postJson(this.verifyPasswordUrl, { email: email, password: password });
    if (!data) return { statusCode: AuthStatus.NETWORK_ERROR };
    return this.resolveVerify(data);
  }

  // → { statusCode: LOGGED_OUT | NETWORK_ERROR }
  //
  // GET door/logout with credentials; ignore the body (it's an HTML/JS redirect,
  // not JSON — so this uses a raw fetch, NOT JsonFetcher). Deliberately does NOT
  // announce: hosts call detector.check() afterwards, and the detector's
  // Person-loggedOut native signal carries the fan-out (the existing AccountPanel
  // pattern). Absolute https so the clearing Set-Cookie lands in the
  // WKWebView/CookieManager store.
  async logout() {
    try {
      await fetch(this.logoutUrl, { method: "GET", credentials: "include" });
      return { statusCode: AuthStatus.LOGGED_OUT };
    } catch (e) {
      return { statusCode: AuthStatus.NETWORK_ERROR };
    }
  }

  // → { statusCode: RESET_SENT | NETWORK_ERROR }
  //
  // Enumeration-uniform: the server ALWAYS answers RESET_SENT for any address (a
  // real account is mailed a reset link; an unknown email is silently ignored), so
  // this method never branches on account existence. Only a transport failure
  // diverges — to NETWORK_ERROR — like every other client method.
  async requestPasswordReset(email) {
    const data = await this.postJson(this.requestPasswordResetUrl, { email: email });
    if (!data) return { statusCode: AuthStatus.NETWORK_ERROR };
    return { statusCode: data.status || AuthStatus.UNKNOWN_ERROR };
  }

  // Shared verify-result handling for the code and password paths.
  resolveVerify(data) {
    const statusCode = data.status || AuthStatus.UNKNOWN_ERROR;
    switch (statusCode) {
      case AuthStatus.AUTHENTICATED:
        // The cross-surface fan-out is a non-negotiable side effect of ANY
        // successful auth (code OR password) — centralised here so no host, and
        // no future host, can forget it. Native-gated + null-guarded inside the
        // primitive (safe no-op on web / where no detector booted).
        window.Rankers_NS?.announceSessionChange?.();
        return { statusCode: statusCode, personId: data.person_id ?? null };
      case AuthStatus.EXPIRED_CODE:
      case AuthStatus.TOO_MANY_ATTEMPTS:
        // The current code is dead — let the user request a fresh one immediately.
        this.allowImmediateResend();
        return { statusCode: statusCode };
      default:
        return { statusCode: statusCode };
    }
  }

  // --- Resend cooldown clock -------------------------------------------------
  //
  // The client owns the cooldown TRUTH; hosts read it and run their own 1s
  // interval to render the "Resend Code (42s)" countdown (ticking is the host's
  // job — see the plan's host recipe).

  canResend() {
    return Date.now() >= this.resendAvailableAt;
  }

  resendSecondsRemaining() {
    return Math.max(0, Math.ceil((this.resendAvailableAt - Date.now()) / 1000));
  }

  startCooldown() {
    this.resendAvailableAt = Date.now() + this.COOLDOWN_MS;
  }

  allowImmediateResend() {
    this.resendAvailableAt = 0;
  }

  // --- Transport -------------------------------------------------------------

  // Never rejects: a timeout / non-OK / unreachable server resolves to null, which
  // every caller maps to NETWORK_ERROR.
  postJson(url, params) {
    return new JsonFetcher(url, { httpMethod: "POST", timeout: REQUEST_TIMEOUT_MS })
      .fetchData(params)
      .catch(() => null);
  }
}
